import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'box-border inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[var(--inv-radius-control,12px)] border-0 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--inv-accent-ring,rgba(0,70,184,0.14))] disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'border-0 bg-[var(--inv-accent)] text-[var(--inv-on-accent)] shadow-[0_6px_18px_rgba(var(--inv-accent-rgb,0,70,184),0.22)] hover:bg-[var(--inv-accent-hover,#00378f)] hover:-translate-y-px hover:shadow-[0_10px_22px_rgba(var(--inv-accent-rgb,0,70,184),0.28)]',
        outline:
          'border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] text-[var(--inv-text-secondary)] shadow-none hover:border-[var(--inv-border-strong,#d6cbc0)] hover:bg-[var(--inv-surface-muted)]',
        secondary:
          'border border-solid border-[var(--inv-accent)] bg-transparent text-[var(--inv-accent)] shadow-none hover:border-[var(--inv-accent-hover,#00378f)] hover:bg-[var(--inv-accent-soft)] hover:text-[var(--inv-accent-hover,#00378f)]',
        ghost:
          'border-0 bg-transparent text-[var(--inv-text-secondary)] shadow-none hover:bg-[var(--inv-surface-muted)]',
        destructive:
          'border-0 bg-[#d92d20] text-white shadow-none hover:bg-[#b42318]',
        icon:
          'h-8 w-8 rounded-md border-0 bg-transparent p-0 text-[#b42318] shadow-none hover:bg-[#fef3f2]',
      },
      size: {
        default: 'h-10 px-4 py-2',
        pill: 'h-11 gap-1.5 rounded-full px-5',
        sm: 'h-9 px-3',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };

